import { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import { SkeletonUtils } from "three-stdlib";
import * as THREE from "three";
import { addEventListener } from "../src/events/eventBus";

export default function Balloons({ position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, ...props }) {
  const group = useRef();
  const { scene, animations } = useGLTF("/balloons.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions, names } = useAnimations(animations, group);
  const [visible, setVisible] = useState(false);
  const actionRef = useRef(null);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.frustumCulled = false;
      }
      if (child.isObject3D) {
        child.frustumCulled = false;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (!actions) return undefined;

  const actionNames = names?.length ? names : Object.keys(actions);
  const targetIndex = 2;
  const actionName = actionNames[targetIndex] ?? actionNames[0];
    if (!actionName) return undefined;

    const action = actions[actionName];
    if (!action) return undefined;

    action.clampWhenFinished = false;
    action.enabled = false;
    actionRef.current = action;

    return () => {
      action.stop();
      action.enabled = false;
      actionRef.current = null;
    };
  }, [actions, names]);

  useEffect(() => {
    if (!group.current) return;
    group.current.rotation.set(rotation[0], rotation[1], rotation[2]);
  }, [rotation]);

  useEffect(() => {
    const unsubscribe = addEventListener("playCake", () => {
      setVisible(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const action = actionRef.current;
    if (!action) return;

    if (visible) {
      action.enabled = true;
      action.reset().setLoop(THREE.LoopRepeat, Infinity).play();
      return;
    }

    action.fadeOut(0.2);
    action.enabled = false;
  }, [visible]);

  return (
    <primitive
      ref={group}
      object={clonedScene}
      position={position}
      visible={visible}
      scale={scale}
      {...props}
    />
  );
}

useGLTF.preload("/balloons.glb");
